import { PlayerProfileStatsSummary } from "./stats";

export class Player {
  canRate?: boolean;
  ratingSummaryIncluded?: boolean;
  ratingAverageOffence?: number;
  ratingAverageDefence?: number;
  viewerRatingOffence?: number;
  viewerRatingDefence?: number;
  id: string;
  name: string;
  email: string;
  nickName: string;
  phoneNumber: string;
  authId: string;
  loginEmail: string;
  playerType: string;
  guestManagerId: string;
  managedByPlayerId: string;
  guest: boolean;
  managedPlayer: boolean;
  userNames: string[];
  active: boolean;
  openProfile: boolean;
  allowTeammatesSOS: boolean;
  allowCitySOS: boolean;
  avatar: string;
  avatarSource: "SOCIAL" | "CUSTOM";
  comments: string[];
  locationInfo: PlayerLocationInfo;
  location: GeoJSON.Point;
  version: number;
  mutedChatIds: string[];
  dismissedClientNotificationIds: string[];
  readClientNotificationIds: string[];
  city: string;
  address: string;
  addressLat: number;
  addressLong: number; 
  preferredLanguage: SupportedLanguage;
  languageSelectionCompleted: boolean;
  emailVerified?: boolean;
  email_verified?: boolean;
  claimed: boolean;
  merged: boolean;
  claimStatus: string;

  autocompleteName: string;
  superAdmin: boolean;

  ratingDefence: Array<{
    ratedBy: string;
    rating: number;
  }>;

  ratingOffence: Array<{
    ratedBy: string;
    rating: number;
  }>;

  profileStatsSummary?: PlayerProfileStatsSummary;
  profileCreated?: boolean;
  networkReferenceCount?: number;
  networkReferenceScore?: number;
  networkReferences?: FootballNetworkReference[];
}

export type SupportedLanguage = 'en' | 'hr';

export interface LanguageUpdateRequest {
  language: SupportedLanguage;
}

export type DirectMatchInviteType = "EXISTING_PLAYER" | "EXISTING_PARTICIPANT" | "NEW_USER_EMAIL";

export interface DirectMatchInviteRequest {
  email?: string;
  nickName?: string;
}

export interface DirectMatchInviteResponse {
  type: DirectMatchInviteType;
  player?: Player;
  email?: string;
  invitePath?: string;
  deliveryType?: "INVITATION" | "REMINDER";
}

export interface EmailMatchInviteRequest {
  emails: string[];
}

export interface EmailMatchInviteResponse {
  sentCount: number;
  invitationCount: number;
  reminderCount: number;
  skippedCount: number;
  emails: string[];
  invitePath?: string;
}

export class PlayerType {
  REGISTERED: string = "REGISTERED";
  MANAGED_PLAYER: string = "MANAGED_PLAYER";
  GUEST: string = "GUEST";
}

export interface GuestPlayerRequest {
  name: string;
  nickName: string;
  status?: string;
  jersey?: string;
  teamMemberType?: string;
}

export interface GuestClaimInvitationRequest {
  email: string;
}

export interface GuestClaimInvitation {
  status: 'PENDING' | 'CLAIMED' | 'REVOKED' | 'EXPIRED' | 'FAILED';
  email: string;
  expiresAt: string;
}

export interface GuestClaimInfo {
  valid: boolean;
  name: string;
  nickName: string;
  expiresAt: string;
}

export interface GuestClaimResult {
  result: 'GUEST_CONVERTED' | 'GUEST_MERGED' | 'ALREADY_CLAIMED';
}

export interface RatingUpdate {
  ratedBy: string;
  rating: number;
}

export interface PublicPlayerProfile {
  sharedViaLink?: boolean;
  id: string;
  name: string;
  nickName: string;
  email?: string;
  phoneNumber?: string;
  avatar?: string;
  city?: string;
  joinedAt?: string | Date;
  ratingAverageDefence?: number;
  ratingAverageOffence?: number;
  stats?: PlayerProfileStatsSummary;
}

export interface PlayerMatchHistoryResponse {
  playerId: string;
  playerName: string;
  playerNickName: string;
  page: number;
  size: number;
  hasMore: boolean;
  items: PlayerMatchHistoryItem[];
}

export interface PlayerMatchHistoryItem {
  id: string;
  name: string;
  originalEventId: string;
  locationName: string;
  playSpaceId: string;
  playSpaceName: string;
  nextOccurrenceTimestamp: Date | string;
  nextOccurrenceTimestampZone: string;
  maxPlayers: number;
  confirmedCount: number;
  whiteScore: number;
  blackScore: number;
  played: boolean;
  cancelled: boolean;
  cancelReason: string;
  canViewRoster: boolean;
  visibilityReason: "SELF" | "OPEN_PROFILE" | "FRIEND" | "SHARED_MATCH" | "NONE";
  gameResults: PlayerMatchHistoryGameResult[];
  participants: PlayerMatchHistoryParticipant[];
}

export interface PlayerMatchHistoryGameResult {
  id?: string;
  roundNumber: number;
  whiteScore: number;
  blackScore: number;
  playerJersey: "WHITE" | "BLACK" | null;
  playerOutcome: "WON" | "LOST" | "DREW" | "NOTHING";
  whitePlayerIds: string[];
  blackPlayerIds: string[];
}

export interface PlayerMatchHistoryParticipant {
  id: string;
  name: string;
  nickName: string;
  avatar: string;
  status: string;
  jersey: "WHITE" | "BLACK";
}

export interface FootballerFriendsResponse {
  page: number;
  size: number;
  total: number;
  hasMore: boolean;
  items: FootballerFriend[];
}

export interface FootballerFriend {
  playerId: string;
  name: string;
  nickName: string;
  avatar: string;
  city: string;
  sharedMatchCount: number;
  sameTeamRoundCount: number;
  togetherWins: number;
  togetherDraws: number;
  togetherLosses: number;
  lastSharedMatchAt: Date | string;
}

export interface FootballReferencesResponse {
  playerId: string;
  playerName: string;
  playerNickName: string;
  page: number;
  size: number;
  total: number;
  hasMore: boolean;
  items: FootballReference[];
}

export interface FootballReference {
  playerId: string;
  name: string;
  nickName: string;
  avatar: string;
  city: string;
  openProfile: boolean;
  referenceScore: number;
  currentSharedMatchCount: number;
  viewedSharedMatchCount: number;
  sameMatchTogetherCount: number;
  lastPlayedWithCurrentAt: Date | string;
  lastPlayedWithViewedAt: Date | string;
}

export interface FootballNetworkCandidatesResponse {
  page: number;
  size: number;
  total: number;
  hasMore: boolean;
  items: FootballNetworkCandidate[];
}

export interface FootballNetworkCandidate {
  playerId: string;
  name: string;
  nickName: string;
  avatar: string;
  city: string;
  referenceCount: number;
  referenceScore: number;
  references: FootballNetworkReference[];
}

export interface FootballNetworkReference {
  playerId: string;
  name: string;
  nickName: string;
  avatar: string;
  currentSharedMatchCount: number;
  candidateSharedMatchCount: number;
  referenceScore: number;
}

export class PlayerDeviceData {
  client: string;
  userPhoneNumber: string;
  userEmail: string;
  userAppPrivateKey: string;
  otherBulkInfo: string;
  pushAccessToken: string;
}

export class PlayerLocationInfo {
  public ip: string;
  public email: string;
  public continentCode: string;
  public continentName: string;
  public countryCode: string;
  public countryName: string;
  public region: string;
  public city: string;
  public latitude: number;
  public longitude: number;
  public zipCode: string;
  public timeZone: string;
  public address: string;
}

export class PlayerPushTokenInfo {
  public email: string;
  public pushAccessToken: string;
}
